import { useCallback } from 'react';
import {
  findBlock,
  updateBlockInTree,
  removeBlockFromTree,
  insertBlockAfter,
  cleanupEmptyContainers,
  ensureCalloutsHaveChildren,
  ensureChoicesHavePaths,
} from '../../../utils/blockUtils';
import { focusEditable } from '../../../utils/focusUtils';
import { htmlToText } from '../../../utils/caretUtils';
import { EDITABLE_TYPES, CONTAINER_TYPES } from '../../../utils/editorConstants';
import { parentOf } from '../../../utils/blockTreeHelpers';
import { isEmptyCodeContent } from '../../../types/CodeBlock/utils/codeContent';

const isDeletedAsUnit = (block) =>
  block?.type === 'divider' ||
  block?.type === 'variables' ||
  block?.type === 'linked-variable-row' ||
  block?.type === 'snippet' ||
  (block?.type === 'code' && isEmptyCodeContent(block.content));

/** Empty checkbox/bullet being removed — nothing on this line worth keeping. */
const isBlankMergeContent = (html) =>
  String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/[\u200B\u00a0]/g, '')
    .replace(/&nbsp;/gi, '')
    .trim() === '';

/** Same-parent sibling list for a block (document root when unparented). */
const siblingList = (tree, blockId) => {
  const parent = parentOf(tree, blockId);
  return parent ? parent.children : tree;
};

/**
 * Backspace/Delete merge across blocks, and Escape out of callout/toggle.
 *
 * Merges only consider the previous/next *sibling* (same parent). Flat
 * document order used to skip container nodes and then merge with content
 * *inside* the neighbouring toggle/callout — e.g. Delete at the end of a
 * paragraph before a toggle would suck the toggle's first child up into the
 * paragraph. Sibling-scoped merge keeps containers intact.
 */
export default function useBlockMerge({ blocksRef, commit }) {
  const handleMergeWithPrevious = useCallback(
    (blockId, currentContent) => {
      const current = blocksRef.current;
      const list = siblingList(current, blockId);
      const index = list.findIndex((b) => b.id === blockId);
      if (index <= 0) return false;

      const neighbour = list[index - 1];
      // Don't steal text out of a preceding toggle/callout/row
      if (CONTAINER_TYPES.includes(neighbour.type)) return false;

      if (isDeletedAsUnit(neighbour)) {
        // Empty list item: caller turns it into a blank line. Don't eat the
        // atomic block above (variable row, divider, …) — that delete is for
        // a line that still has text.
        if (isBlankMergeContent(currentContent)) return false;
        commit(cleanupEmptyContainers(removeBlockFromTree(current, neighbour.id)));
        focusEditable(blockId, { atStart: true });
        return true;
      }

      if (neighbour.type === 'code') {
        focusEditable(neighbour.id);
        return true;
      }

      if (!EDITABLE_TYPES.includes(neighbour.type)) return false;

      const junction = htmlToText(neighbour.content).length;
      let tree = updateBlockInTree(current, neighbour.id, {
        content: (neighbour.content || '') + (currentContent || ''),
      });

      const parent = parentOf(current, blockId);
      const idToRemove =
        parent?.type === 'callout' && parent.children.length === 1 ? parent.id : blockId;
      tree = cleanupEmptyContainers(removeBlockFromTree(tree, idToRemove));

      commit(tree);
      focusEditable(neighbour.id, { offset: junction });
      return true;
    },
    [commit, blocksRef]
  );

  // Delete at end of a line: pull the next sibling's text up into this block
  // (paragraphs, bullets, numbers, checkboxes, headings). `currentContent` is
  // the live field HTML when the tree may still be catching up to a coalesced
  // keystroke.
  const handleMergeWithNext = useCallback(
    (blockId, currentContent) => {
      const current = blocksRef.current;
      const list = siblingList(current, blockId);
      const index = list.findIndex((b) => b.id === blockId);
      if (index < 0 || index >= list.length - 1) return false;

      const block = list[index];
      if (!EDITABLE_TYPES.includes(block.type)) return false;

      const neighbour = list[index + 1];
      // Don't pull content out of a following toggle/callout/row
      if (CONTAINER_TYPES.includes(neighbour.type)) return false;

      if (isDeletedAsUnit(neighbour)) {
        commit(cleanupEmptyContainers(removeBlockFromTree(current, neighbour.id)));
        return true;
      }

      if (neighbour.type === 'code') {
        focusEditable(neighbour.id, { atStart: true });
        return true;
      }

      if (!EDITABLE_TYPES.includes(neighbour.type)) return false;

      const before =
        currentContent != null ? currentContent : block.content || '';
      const junction = htmlToText(before).length;
      let tree = updateBlockInTree(current, blockId, {
        content: before + (neighbour.content || ''),
      });

      const parent = parentOf(current, neighbour.id);
      const idToRemove =
        parent?.type === 'callout' && parent.children.length === 1
          ? parent.id
          : neighbour.id;
      tree = cleanupEmptyContainers(removeBlockFromTree(tree, idToRemove));

      commit(tree);
      focusEditable(blockId, { offset: junction });
      return true;
    },
    [commit, blocksRef]
  );

  const handleEscapeContainer = useCallback(
    (blockId) => {
      const current = blocksRef.current;
      const parent = parentOf(current, blockId);
      if (parent?.type === 'choice-path') {
        const choice = parentOf(current, parent.id);
        if (choice?.type !== 'choice') return false;
        if (parent.children[parent.children.length - 1]?.id !== blockId) return false;
        const block = findBlock(current, blockId);
        if (!block) return false;
        let tree = removeBlockFromTree(current, blockId);
        tree = insertBlockAfter(tree, choice.id, block);
        tree = ensureChoicesHavePaths(ensureCalloutsHaveChildren(tree));
        commit(tree);
        focusEditable(blockId);
        return true;
      }
      if (parent?.type !== 'callout' && parent?.type !== 'toggle') return false;
      if (parent.children[parent.children.length - 1]?.id !== blockId) return false;

      const block = findBlock(current, blockId);
      if (!block) return false;
      let tree = removeBlockFromTree(current, blockId);
      tree = insertBlockAfter(tree, parent.id, block);
      tree = ensureCalloutsHaveChildren(tree);
      commit(tree);
      focusEditable(blockId);
      return true;
    },
    [commit, blocksRef]
  );

  return { handleMergeWithPrevious, handleMergeWithNext, handleEscapeContainer };
}
